import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import CodeMirror from '@uiw/react-codemirror';
import { oneDark } from '@codemirror/theme-one-dark';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { java } from '@codemirror/lang-java';
import { CODING_TEMPLATES } from '../../pages/assessments/codingAssignmentTemplates';
import PersonalityProfilePanel from './PersonalityProfilePanel';
import { PERSONALITY_KIND } from './personalityProfile';

const CODING_TEMPLATE_BY_ID = CODING_TEMPLATES.reduce((acc, tpl) => {
  const k = String(tpl.id || '').trim().toLowerCase();
  if (k) acc[k] = tpl;
  return acc;
}, {});

const CODING_TEMPLATE_BY_TITLE = CODING_TEMPLATES.reduce((acc, tpl) => {
  const k = String(tpl.title || '').trim().toLowerCase();
  if (k) acc[k] = tpl;
  return acc;
}, {});

function formatScore(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return n;
  return parseFloat(n.toFixed(2));
}

function getCatalogCodingMetaForResult(result) {
  const tid = String(result?.question?.codingMeta?.templateId || '')
    .trim()
    .toLowerCase();
  const titleKey = String(result?.question?.title || '')
    .trim()
    .toLowerCase();
  return (tid && CODING_TEMPLATE_BY_ID[tid]) || CODING_TEMPLATE_BY_TITLE[titleKey] || null;
}

// ---------------------------------------------------------------------------
// Pure helpers – no dependency on React or API shape beyond the result object
// ---------------------------------------------------------------------------

/**
 * Derives total score from details when API does not provide a top-level score.
 * Sums earnedPoints (or infers from isCorrect + points) across results.
 */
export function getDetailScore(details) {
  if (!details) return 0;
  if (typeof details.score === 'number') return details.score;
  const results = Array.isArray(details.results) ? details.results : [];
  if (results.length === 0) return 0;
  return results.reduce((sum, r) => {
    if (typeof r.earnedPoints === 'number') return sum + r.earnedPoints;
    if (r.isCorrect) return sum + (typeof r.points === 'number' ? r.points : 1);
    return sum;
  }, 0);
}

/**
 * Converts option text(s) to a,b,c format for MCQ display.
 * For multiple-answer questions, letters are sorted (A, B, C order).
 */
export function formatUserAnswerAsLetters(userAnswer, options) {
  if (!options || !Array.isArray(options) || options.length === 0) return null;
  const answers = Array.isArray(userAnswer) ? userAnswer : userAnswer ? [userAnswer] : [];
  if (answers.length === 0) return null;
  const letters = answers
    .map((text) => {
      const idx = options.findIndex((opt) => opt?.text?.trim() === String(text).trim());
      return idx >= 0 ? String.fromCharCode(97 + idx) : null;
    })
    .filter(Boolean);
  if (letters.length > 1) letters.sort();
  return letters.length > 0 ? letters.join(', ') : null;
}

/**
 * Correct answer as a,b,c for MCQ when options exist; otherwise raw value.
 */
export function formatCorrectAnswerAsLetters(correctAnswer, options) {
  if (options && Array.isArray(options) && options.length > 0) {
    const letters = options
      .map((opt, idx) => (opt?.isCorrect ? String.fromCharCode(97 + idx) : null))
      .filter(Boolean);
    if (letters.length > 0) return letters.join(', ');
  }
  return correctAnswer;
}

function formatRenderableAnswer(answer) {
  if (answer == null || answer === '') return 'No answer';
  if (Array.isArray(answer)) return answer.join(', ');
  if (typeof answer === 'object') {
    if (typeof answer.code === 'string') return answer.code || 'No answer';
    if (typeof answer.text === 'string') return answer.text || 'No answer';
    return JSON.stringify(answer);
  }
  return answer;
}

function getCodeFromAnswer(answer) {
  if (!answer) return '';
  if (typeof answer === 'object' && typeof answer.code === 'string') return answer.code;
  if (typeof answer === 'string') return answer;
  return '';
}

function getCodeLanguage(result) {
  const fromAnswer =
    result?.userAnswer && typeof result.userAnswer === 'object' ? result.userAnswer.language : null;
  const fromMeta = result?.question?.codingMeta?.allowedLanguages?.[0];
  return String(fromAnswer || fromMeta || 'javascript').toLowerCase();
}

function getCodeExtensions(lang) {
  if (lang === 'python') return [python()];
  if (lang === 'java') return [java()];
  return [javascript()];
}

function getCodeCorrectAnswer(result) {
  const lang = getCodeLanguage(result);
  const expectedByLanguage = result?.question?.codingMeta?.expectedByLanguage || {};
  return (
    expectedByLanguage[lang] ||
    expectedByLanguage.javascript ||
    expectedByLanguage.nodejs ||
    expectedByLanguage.python ||
    expectedByLanguage.java ||
    result?.correctAnswer ||
    ''
  );
}

function getCodeExpectedOutput(result) {
  const catalog = getCatalogCodingMetaForResult(result);
  const catalogSample = catalog?.sampleOutput || '';

  const fallbackByTemplateId = {
    'remove-duplicates-sorted-array': '5',
    'even-number-of-digits': '2',
    'count-vowels-string': '5',
    'string-length': '5',
    'prime-number-check': 'true',
    'find-maximum-element-array': '9',
    'flatten-nested-object-api': '{"user.id":42,"user.active":false,"region":"us-east"}',
  };
  const templateId = String(result?.question?.codingMeta?.templateId || '')
    .trim()
    .toLowerCase();
  const title = String(result?.question?.title || '')
    .trim()
    .toLowerCase();
  const fallbackByTitle = title.includes('count vowels')
    ? '5'
    : title.includes('remove duplicates')
      ? '5'
      : title.includes('even number of digits')
        ? '2'
        : title.includes('string length') || title.includes('length of the string') || title.includes('length of string')
          ? '5'
          : title.includes('prime number')
            ? 'true'
            : title.includes('maximum element')
              ? '9'
              : title.includes('flatten nested object') || title.includes('flatten nested')
                ? '{"user.id":42,"user.active":false,"region":"us-east"}'
                : '';

  return (
    result?.question?.codingMeta?.sampleOutput ||
    result?.question?.codingMeta?.expectedOutput ||
    catalogSample ||
    fallbackByTemplateId[templateId] ||
    fallbackByTitle ||
    ''
  );
}

function getCodeOutput(result) {
  const fromResult = result?.codeOutput;
  if (typeof fromResult === 'string') return fromResult;
  const fromAnswer =
    result?.userAnswer && typeof result.userAnswer === 'object' ? result.userAnswer.output : '';
  return typeof fromAnswer === 'string' ? fromAnswer : '';
}

/** Must stay in sync with server MIN_CODE_SIMILARITY_WHEN_OUTPUT_MATCHES (_coreAssessments.ctrl.js). */
const MIN_CODE_IMPL_SIMILARITY = 0.22;

/**
 * True when printed output matched the sample but the submission failed the implementation check
 * (similarity to reference below threshold — e.g. console.log(fixed answer)). Not shown when the
 * candidate passed that bar but scored lower for other reasons, or for legacy rows with partial
 * credit above this floor.
 */
function isHardcodedStyleCodeFailure(result) {
  if (String(result?.question?.type || '').toLowerCase() !== 'code') return false;
  if (result.isCorrect) return false;
  if (result.outputMatched !== true) return false;
  const pts = typeof result.points === 'number' ? result.points : 1;
  const earned = typeof result.earnedPoints === 'number' ? result.earnedPoints : 0;
  return earned < pts * MIN_CODE_IMPL_SIMILARITY;
}

function getDisplayedExpectedOutput(result) {
  const fromMeta = getCodeExpectedOutput(result);
  if (fromMeta) return fromMeta;
  const stored = result?.expectedOutput;
  return typeof stored === 'string' ? stored : '';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the "Assessment Score" tab content for the Candidate Results modal.
 * Handles loading, error, and success states. Expects the same payload shape
 * as GET /api/assessments/:id/results/:email.
 *
 * @param {Object} props
 * @param {Object|null} props.details - API response: { results?, error?, message?, totalPoints?, score?, ... }
 * @param {number} [props.totalPointsFallback] - Used when details.totalPoints is missing (e.g. from assessment meta)
 * @param {number} [props.initialModuleIdx] - For multi-module assessments, open on this module tab.
 */
function CandidateAssessmentScoreContent({ details, totalPointsFallback, initialModuleIdx = 0 }) {
  const [openQuestionIds, setOpenQuestionIds] = useState([]);
  const [activeModuleIdx, setActiveModuleIdx] = useState(0); // selected module tab (multi-module)
  const [showRescoreBtn, setShowRescoreBtn] = useState(false);
  const [rescoringId, setRescoringId] = useState(null);
  const { user } = useAuth();
  
  const isAjantaPharma = user?.company === 'Ajanta Pharma' || user?.orgId === '69e1fbda6ae5d218a4beb045' || (user?.orgId && typeof user.orgId === 'object' && (user.orgId._id === '69e1fbda6ae5d218a4beb045' || user.orgId.slug === 'ajantapharma-80unds'));

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault();
        setShowRescoreBtn((prev) => {
          const next = !prev;
          toast.success(`Easter Egg: Admin Rescore button ${next ? 'Enabled' : 'Disabled'}`);
          return next;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleRescore = async (questionId) => {
    const assessmentId = details?.assessment?._id;
    const candidateEmail = details?.candidate?.email;

    if (!assessmentId || !candidateEmail) {
      toast.error('Assessment ID or Candidate Email not found in details.');
      return;
    }

    setRescoringId(questionId);
    try {
      await axios.post(`/api/assessments/${assessmentId}/results/${candidateEmail}/rescore-passage`, {
        questionId
      });
      toast.success('Successfully rescored passage reading!');
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to rescore.');
    } finally {
      setRescoringId(null);
    }
  };

  const handleDownloadWord = async (moduleId, moduleTitle) => {
    const assessmentId = details?.assessment?._id;
    const candidateEmail = details?.candidate?.email;
    if (!assessmentId || !candidateEmail) {
      toast.error('Cannot download: missing assessment or candidate details.');
      return;
    }
    const toastId = toast.loading(`Generating Word document for ${moduleTitle}...`);
    try {
      const response = await axios.get(
        `/api/assessments/${assessmentId}/results/${candidateEmail}/export-word/${moduleId}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${details?.candidate?.name || 'Candidate'}_${moduleTitle}_Results.doc`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Document downloaded successfully!', { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate Word document.', { id: toastId });
    }
  };

  useEffect(() => {
    if (typeof initialModuleIdx === 'number' && initialModuleIdx >= 0) {
      setActiveModuleIdx(initialModuleIdx);
    }
  }, [initialModuleIdx, details?.candidate?.email, details?.assessment?.isMultiModule]);

  const SkeletonBlock = ({ className = '' }) => (
    <div className={`animate-pulse rounded-md bg-gray-200/90 ${className}`} />
  );

  const results = Array.isArray(details?.results) ? details.results : [];
  const isMultiModule = details?.assessment?.isMultiModule === true && !details?.assessment?.isRubricBased;
  const modules = details?.assessment?.modules || [];
  const moduleScores = details?.candidate?.moduleScores || [];

  // For multi-module, each module is a tab. The questions/answers for the active module come from
  // its normalized `results` (built server-side); the overall score sums every module.
  const moduleScoreFor = (mod) =>
    moduleScores.find(
      (s) => String(s.moduleId) === String(mod?.libraryTestId?._id || mod?.libraryTestId || mod?._id || mod?.id)
    );
  const safeModuleIdx = isMultiModule && modules.length > 0
    ? Math.min(activeModuleIdx, modules.length - 1)
    : 0;
  const activeModule = isMultiModule ? modules[safeModuleIdx] || null : null;
  const activeModuleScore = activeModule ? moduleScoreFor(activeModule) : null;
  const displayResults = isMultiModule ? activeModuleScore?.results || [] : results;
  const overallEarned = isMultiModule
    ? moduleScores.reduce((sum, m) => sum + (m.score || 0), 0)
    : null;
  const overallTotal = isMultiModule
    ? moduleScores.reduce((sum, m) => sum + (m.maxScore || 0), 0)
    : null;

  useEffect(() => {
    const list = isMultiModule ? displayResults : results;
    if (!details || details.error || list.length === 0) {
      setOpenQuestionIds([]);
      return;
    }
    const firstIncorrectIndex = list.findIndex((r) => !r.isCorrect);
    const defaultIndex = firstIncorrectIndex >= 0 ? firstIncorrectIndex : 0;
    const defaultKey = String(
      list[defaultIndex].questionId ?? list[defaultIndex]._id ?? defaultIndex
    );
    setOpenQuestionIds([defaultKey]);
  }, [details, safeModuleIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  if (details?.notAttempted) {
    return (
      <div className="py-16 text-center text-gray-500">
        <p className="text-base font-semibold text-red-600">Candidate not attempted.</p>
      </div>
    );
  }

  if (details?.error) {
    return (
      <div className="py-16 text-center text-red-600">
        {details.message ?? 'Something went wrong.'}
      </div>
    );
  }

  if (!details) {
    return (
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <SkeletonBlock className="h-6 w-44" />
            <SkeletonBlock className="h-7 w-24 rounded-full" />
          </div>
        </div>
        <div className="p-5 space-y-3">
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={`assessment-loading-skeleton-${idx}`} className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <SkeletonBlock className="h-5 w-36" />
                <SkeletonBlock className="h-5 w-16 rounded-full" />
              </div>
              <SkeletonBlock className="h-4 w-full mb-2" />
              <SkeletonBlock className="h-4 w-4/5 mb-2" />
              <SkeletonBlock className="h-4 w-3/5" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Standalone personality (IPIP-50): a trait profile, never a score.
  if (details?.assessment?.assessmentKind === PERSONALITY_KIND) {
    return (
      <div className="bg-white rounded-xl border border-gray-200">
        <PersonalityProfilePanel result={details.candidate?.personalityResult} answers={results} />
      </div>
    );
  }

  const score = getDetailScore(details);

  // Calculate total points from results if not provided by API
  const totalPointsFromResults = results.length > 0
    ? results.reduce((sum, r) => sum + (typeof r.points === 'number' ? r.points : 1), 0)
    : null;

  const totalPoints =
    details.totalPoints ?? totalPointsFallback ?? totalPointsFromResults ?? 'N/A';
  const totalNumeric =
    typeof totalPoints === 'number'
      ? totalPoints
      : typeof totalPointsFallback === 'number'
        ? totalPointsFallback
        : 100;
  const percentage = totalNumeric > 0 ? Math.round((score / totalNumeric) * 100) : 0;
  const toggleQuestion = (questionKey) => {
    setOpenQuestionIds((prev) =>
      prev.includes(questionKey) ? prev.filter((id) => id !== questionKey) : [...prev, questionKey]
    );
  };

  if (!isMultiModule && results.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-900">Question Results</h4>
            <div className="flex items-center gap-2">
              <AcademicCapIcon className="h-5 w-5 text-blue-600" />
              <span className="text-lg font-bold text-gray-900">
                Score: {formatScore(score)}/{totalPoints ?? 'N/A'}
              </span>
              {typeof totalPoints === 'number' && (
                <span className="ml-2 px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                  {percentage}%
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="px-6 py-8 text-center text-gray-500 text-sm">
          No question results available.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-900">
            {isMultiModule ? 'Assessment Results' : 'Question Results'}
          </h4>
          <div className="flex items-center gap-2">
            <AcademicCapIcon className="h-5 w-5 text-blue-600" />
            <span className="text-lg font-bold text-gray-900">
              Score: {formatScore(isMultiModule ? overallEarned : score)}/
              {isMultiModule ? (overallTotal ?? 'N/A') : (totalPoints ?? 'N/A')}
            </span>
            <span className="ml-2 px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
              {isMultiModule
                ? overallTotal > 0
                  ? Math.round((overallEarned / overallTotal) * 100)
                  : 0
                : percentage}
              %
            </span>
          </div>
        </div>

        {/* One tab per module, in creation order. Each shows that module's questions + score. */}
        {isMultiModule && (
          <div className="mt-4 flex flex-wrap gap-2">
            {modules.map((mod, i) => {
              const ms = moduleScoreFor(mod);
              const active = safeModuleIdx === i;
              return (
                <button
                  key={i}
                  onClick={() => setActiveModuleIdx(i)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                    active
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span>
                    {mod.title || `Module ${i + 1}`}
                    {ms?.personalityResult
                      ? ' · Profile'
                      : ms ? ` · ${formatScore(ms.score)}/${ms.maxScore ?? '–'}` : ''}
                  </span>
                  {isAjantaPharma && (
                    <button
                      type="button"
                      title={`Download Word Doc for ${mod.title || `Module ${i + 1}`}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadWord(mod.libraryTestId?._id || mod.libraryTestId || mod._id || mod.id, mod.title || `Module ${i + 1}`);
                      }}
                      className="ml-1 p-1 rounded-md hover:bg-indigo-100 transition-colors"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 text-indigo-600" />
                    </button>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {isMultiModule && activeModuleScore?.personalityResult && (
        <PersonalityProfilePanel result={activeModuleScore.personalityResult} answers={displayResults} />
      )}

      {isMultiModule && !activeModuleScore?.personalityResult && displayResults.length === 0 && (
        <div className="px-6 py-10 text-center text-sm text-gray-500">
          {activeModuleScore
            ? 'No question details available for this module.'
            : 'This module has not been completed yet.'}
        </div>
      )}

      {(!isMultiModule || (displayResults.length > 0 && !activeModuleScore?.personalityResult)) && (
      <div className="p-4 space-y-3">
        {displayResults.map((result, index) => (
          (() => {
            const questionKey = String(result.questionId ?? result._id ?? index);
            const isOpen = openQuestionIds.includes(questionKey);
            const rowTheme = result.isCorrect
              ? 'border-green-200 bg-green-50/70'
              : 'border-red-200 bg-red-50/70';
            const badgeTheme = result.isCorrect
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700';

            return (
              <div key={questionKey} className={`rounded-xl border ${rowTheme}`}>
                <button
                  type="button"
                  onClick={() => toggleQuestion(questionKey)}
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold text-gray-900">
                          Question {result.questionNumber ?? index + 1}
                        </span>
                        <span className="px-2 py-0.5 text-xs rounded-md bg-white/70 text-gray-700 border border-gray-200">
                          {result.points ?? 1} point{(result.points ?? 1) !== 1 ? 's' : ''}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${badgeTheme}`}>
                          {formatScore(result.earnedPoints ?? 0)}/{result.points ?? 1}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-gray-800 line-clamp-2">
                        {result.question?.title ?? result.question?.content ?? 'Question'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {result.isCorrect ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-600" />
                      )}
                      <ChevronDownIcon
                        className={`h-5 w-5 text-gray-500 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4">
                    {result.question?.options && result.question.options.length > 0 && (
                      <div className="mb-3 rounded-lg border border-gray-200 bg-white/70 p-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">Options</p>
                        <div className="space-y-1">
                          {result.question.options.map((option, optIndex) => (
                            <div key={optIndex} className="flex items-center gap-2 text-xs">
                              <span
                                className={`w-4 h-4 rounded flex items-center justify-center ${
                                  option.isCorrect
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {String.fromCharCode(65 + optIndex)}
                              </span>
                              <span
                                className={
                                  option.isCorrect ? 'text-green-700 font-medium' : 'text-gray-700'
                                }
                              >
                                {option.text}
                                {option.isCorrect && <span className="ml-1 text-green-600">✓</span>}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {(String(result.question?.type || '').toLowerCase() === 'code' ||
                        (result.userAnswer &&
                          typeof result.userAnswer === 'object' &&
                          typeof result.userAnswer.code === 'string')) ? (
                        <div className="md:col-span-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-700">Your Answer:</span>
                            <span className="text-xs text-gray-500 uppercase">
                              {getCodeLanguage(result)}
                            </span>
                          </div>
                          <div
                            className={`rounded-lg overflow-hidden border ${
                              result.isCorrect ? 'border-green-200' : 'border-red-200'
                            }`}
                          >
                            <CodeMirror
                              value={getCodeFromAnswer(result.userAnswer) || '// No answer'}
                              height="220px"
                              theme={oneDark}
                              editable={false}
                              basicSetup={{
                                lineNumbers: true,
                                foldGutter: true,
                                highlightActiveLine: false,
                              }}
                              extensions={getCodeExtensions(getCodeLanguage(result))}
                            />
                          </div>
                        </div>
                      ) : (result.passageReading ||
                        result.audioUrl ||
                        String(result.userAnswer || '').includes('[audio-recording:') ||
                        (typeof result.userAnswer === 'object' && (result.userAnswer?.audioData || result.userAnswer?.audioS3Key)) ||
                        String(result.question?.type || '').toLowerCase().includes('passage')) ? (
                        <div className="md:col-span-2 space-y-3">
                          {/* Passage Text */}
                          {result.question?.passageMeta?.passageText && (
                            <div style={{
                              background: '#f8fafc', border: '1px solid #e2e8f0',
                              borderRadius: '10px', padding: '14px 16px',
                            }}>
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px',
                              }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
                                <span style={{ fontWeight: 600, fontSize: '12px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Reference Passage
                                </span>
                              </div>
                              <p style={{
                                fontSize: '13px', lineHeight: 1.7, color: '#475569',
                                margin: 0, fontStyle: 'italic',
                              }}>
                                "{result.question.passageMeta.passageText}"
                              </p>
                            </div>
                          )}

                          {/* Audio Response Badge + Inline Player */}
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '6px 12px', borderRadius: '10px',
                            background: '#f0f4ff', border: '1px solid #dbeafe',
                          }}>
                            <div style={{
                              width: 26, height: 26, borderRadius: '6px',
                              background: 'linear-gradient(135deg, #6366f1, #818cf8)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
                            </div>
                            <div style={{ flexShrink: 0, marginRight: '4px' }}>
                              <div style={{ fontWeight: 600, fontSize: '11px', color: '#334155', lineHeight: 1.2 }}>
                                Audio Response
                              </div>
                              
                            </div>
                            {result.audioUrl && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                <audio
                                  controls
                                  src={result.audioUrl}
                                  style={{
                                    flex: 1, height: '32px', minWidth: 0,
                                    borderRadius: '6px',
                                  }}
                                />
                                {showRescoreBtn && (
                                  <button
                                    type="button"
                                    disabled={rescoringId === questionKey}
                                    onClick={() => handleRescore(result.questionId || result.question?._id)}
                                    className={`px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition-all ${
                                      rescoringId === questionKey ? 'opacity-70 cursor-not-allowed' : ''
                                    }`}
                                  >
                                    {rescoringId === questionKey ? 'Rescoring...' : 'Rescore Speech'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="font-medium text-gray-700">Your Answer: </span>
                          <span className={result.isCorrect ? 'text-green-700' : 'text-red-700'}>
                            {formatUserAnswerAsLetters(result.userAnswer, result.question?.options) ??
                              formatRenderableAnswer(result.userAnswer)}
                          </span>
                        </div>
                      )}

                      {/* Points & Correct Answer — hide for Passage Reading */}
                      {!String(result.question?.type || '').toLowerCase().includes('passage') && (
                        <>
                          <div>
                            <span className="font-medium text-gray-700">Points: </span>
                            <span className="text-gray-700">
                              {formatScore(result.earnedPoints ?? 0)}/{result.points ?? 1} earned
                            </span>
                          </div>
                          {String(result.question?.type || '').toLowerCase() === 'code' && (
                            <div className="md:col-span-2">
                              <span className="font-medium text-gray-700">Code Output: </span>
                              <span className="text-gray-700">
                                {getCodeOutput(result) || 'No output captured'}
                              </span>
                            </div>
                          )}
                          {((String(result.question?.type || '').toLowerCase() === 'code' &&
                            (getDisplayedExpectedOutput(result) || getCodeCorrectAnswer(result))) ||
                            result.correctAnswer != null ||
                            result.question?.options?.some((o) => o?.isCorrect)) && (
                            <div className="md:col-span-2">
                              <span className="font-medium text-gray-700">
                                {String(result.question?.type || '').toLowerCase() === 'code'
                                  ? 'Expected Output: '
                                  : 'Correct Answer: '}
                              </span>
                              {String(result.question?.type || '').toLowerCase() === 'code' ? (
                                <span className="text-green-700">
                                  {getDisplayedExpectedOutput(result) || 'No expected output configured'}
                                </span>
                              ) : (
                                <span className="text-green-700">
                                  {formatCorrectAnswerAsLetters(result.correctAnswer, result.question?.options)}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {isHardcodedStyleCodeFailure(result) && (
                        <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
                          <p className="font-semibold">Not accepted: output matches the sample only</p>
                          <p className="mt-1.5 text-amber-900/90 leading-snug">
                            Your program printed the expected value, but it does not count as a correct answer
                            because the implementation does not solve the task (for example logging a fixed value,
                            printing the input, or otherwise bypassing the required logic). Write code that
                            actually computes the expected output for the problem.
                          </p>
                          {String(getCodeCorrectAnswer(result) || '').trim() ? (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-amber-950 uppercase tracking-wide">
                                Reference solution
                              </p>
                              <pre className="mt-1.5 max-h-56 overflow-auto rounded-md border border-amber-200/80 bg-gray-900 p-3 text-left text-xs text-gray-100 whitespace-pre-wrap font-mono">
                                {getCodeCorrectAnswer(result)}
                              </pre>
                            </div>
                          ) : null}
                        </div>
                      )}
                      {/* Question Type — hide for Passage Reading */}
                      {result.question?.type && !String(result.question?.type || '').toLowerCase().includes('passage') && (
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700">Question Type: </span>
                          <span className="text-gray-700 capitalize">{result.question.type}</span>
                        </div>
                      )}
                    </div>
                    {result.explanation && (
                      <p className="mt-2 text-xs text-gray-600">{result.explanation}</p>
                    )}

                    {/* ── Passage Reading: AI Evaluation Results ── */}
                    {result.passageReading && (
                      <div className="mt-4 space-y-3">
                        {/* Overall Score + Dimension Scores — compact row */}
                        <div style={{
                          display: 'flex', gap: '12px', alignItems: 'stretch',
                        }}>
                          {/* Overall Score Card */}
                          <div style={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                            borderRadius: '12px', padding: '16px 20px',
                            display: 'flex', alignItems: 'center', gap: '14px',
                            minWidth: '200px', flex: '0 0 auto',
                          }}>
                            <div style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
                              <svg viewBox="0 0 56 56" width="56" height="56">
                                <circle cx="28" cy="28" r="23" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="5" />
                                <circle
                                  cx="28" cy="28" r="23" fill="none" stroke="#fff" strokeWidth="5"
                                  strokeLinecap="round"
                                  strokeDasharray={`${(result.passageReading.scores?.overall || 0) / 100 * 2 * Math.PI * 23} ${2 * Math.PI * 23}`}
                                  transform="rotate(-90 28 28)"
                                  style={{ transition: 'stroke-dasharray 0.8s ease' }}
                                />
                              </svg>
                              <div style={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 800, fontSize: '15px', color: '#fff',
                              }}>
                                {result.passageReading.scores?.overall ?? 0}%
                              </div>
                            </div>
                            <div>
                              <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                                Overall Score
                              </div>
                              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px', marginTop: 2 }}>
                                Voice Assessment
                              </div>
                            </div>
                          </div>

                          {/* Dimension Score Pills */}
                          <div style={{
                            display: 'flex', gap: '8px', flex: 1, flexWrap: 'wrap',
                          }}>
                            {[
                              { label: 'Accuracy', key: 'accuracy', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
                              { label: 'Fluency', key: 'fluency', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
                              { label: 'Completeness', key: 'completeness', color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd' },
                              { label: 'Pronunciation', key: 'pronunciation', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
                            ].map((dim) => {
                              const val = result.passageReading.scores?.[dim.key] ?? 0;
                              return (
                                <div key={dim.key} style={{
                                  background: dim.bg, border: `1px solid ${dim.border}`,
                                  borderRadius: '10px', padding: '10px 12px',
                                  display: 'flex', alignItems: 'center', gap: '10px',
                                  flex: '1 1 0', minWidth: '120px',
                                }}>
                                  <div style={{ position: 'relative', width: 38, height: 38, flexShrink: 0 }}>
                                    <svg viewBox="0 0 38 38" width="38" height="38">
                                      <circle cx="19" cy="19" r="15" fill="none" stroke={`${dim.color}20`} strokeWidth="4" />
                                      <circle
                                        cx="19" cy="19" r="15" fill="none" stroke={dim.color} strokeWidth="4"
                                        strokeLinecap="round"
                                        strokeDasharray={`${val / 100 * 2 * Math.PI * 15} ${2 * Math.PI * 15}`}
                                        transform="rotate(-90 19 19)"
                                        style={{ transition: 'stroke-dasharray 0.8s ease' }}
                                      />
                                    </svg>
                                    <div style={{
                                      position: 'absolute', inset: 0,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontWeight: 700, fontSize: '10px', color: dim.color,
                                    }}>
                                      {val}%
                                    </div>
                                  </div>
                                  <div style={{
                                    fontSize: '11px', fontWeight: 600, color: dim.color,
                                    textTransform: 'uppercase', letterSpacing: '0.02em',
                                  }}>
                                    {dim.label}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>


                        {/* Disfluency Details (filler words, stammered words) */}
                        {result.passageReading.disfluencyAnalysis && (
                          (result.passageReading.disfluencyAnalysis.fillerWordCount > 0 ||
                           (result.passageReading.disfluencyAnalysis.stammeredWords?.length || 0) > 0) && (
                          <div style={{
                            display: 'flex', gap: '8px', flexWrap: 'wrap',
                          }}>
                            {result.passageReading.disfluencyAnalysis.fillerWordCount > 0 && (
                              <div style={{
                                background: '#fef2f2', border: '1px solid #fecaca',
                                borderRadius: '8px', padding: '8px 12px',
                                flex: '1 1 0', minWidth: '140px',
                              }}>
                                <div style={{ fontWeight: 600, fontSize: '11px', color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '4px' }}>
                                  Filler Words: {result.passageReading.disfluencyAnalysis.fillerWordCount}
                                </div>
                                <div style={{ fontSize: '12px', color: '#b91c1c' }}>
                                  {(result.passageReading.disfluencyAnalysis.fillerWords || []).map((w, i) => (
                                    <span key={i} style={{
                                      display: 'inline-block', background: '#fee2e2', borderRadius: '4px',
                                      padding: '1px 6px', margin: '2px 3px 2px 0', fontSize: '11px',
                                    }}>"{w}"</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {(result.passageReading.disfluencyAnalysis.stammeredWords?.length || 0) > 0 && (
                              <div style={{
                                background: '#fff7ed', border: '1px solid #fed7aa',
                                borderRadius: '8px', padding: '8px 12px',
                                flex: '1 1 0', minWidth: '140px',
                              }}>
                                <div style={{ fontWeight: 600, fontSize: '11px', color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '4px' }}>
                                  Stammered Words: {result.passageReading.disfluencyAnalysis.stammeredWords.length}
                                </div>
                                <div style={{ fontSize: '12px', color: '#c2410c' }}>
                                  {result.passageReading.disfluencyAnalysis.stammeredWords.map((s, i) => (
                                    <div key={i} style={{ marginBottom: '2px' }}>
                                      <span style={{ fontWeight: 600 }}>"{s.word}"</span>
                                      <span style={{ color: '#9a3412', fontSize: '11px' }}>
                                        {' '}{s.attempts} attempts: {(s.fragments || []).map((f, j) => (
                                          <span key={j}>{j > 0 ? ' → ' : ''}<span style={{
                                            background: '#ffedd5', borderRadius: '3px', padding: '0 4px',
                                          }}>"{f}"</span></span>
                                        ))}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          )
                        )}

                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()
        ))}
      </div>
      )}
    </div>
  );
}

export default CandidateAssessmentScoreContent;
